/**
 * SMS 인증 코드 요청 버튼
 * Firebase Phone Authentication용
 *
 * @see SPEC-001-firebase-phone-auth.md
 */
import { useLanguage } from '@/contexts/AppContext';
import { loginPhoneAuthTranslations } from '@/i18n/loginPage';

interface SmsAuthButtonProps {
  isEnabled: boolean;
  isLoading?: boolean;
  onClick: () => void;
  label?: string;
  loadingLabel?: string;
  variant?: 'primary' | 'outline';
}

/**
 * SMS 인증 코드 요청 버튼
 */
export function SmsAuthButton({ isEnabled, isLoading = false, onClick, label, loadingLabel, variant = 'primary' }: SmsAuthButtonProps) {
  const { locale } = useLanguage();
  const t = loginPhoneAuthTranslations[locale];

  const buttonText = isLoading ? (loadingLabel ?? t.sendingCode) : (label ?? t.getCodeViaSms);
  const enabledClass = variant === 'primary'
    ? 'bg-primary hover:bg-primary-hover active:bg-primary/90 text-white hover:shadow-lg hover:shadow-primary/20 active:scale-[0.98] cursor-pointer'
    : 'bg-background border border-primary text-primary hover:bg-primary/5 active:scale-[0.98] cursor-pointer';

  return (
    <button
      onClick={() => {
        if (isEnabled && !isLoading) {
          onClick();
        }
      }}
      disabled={!isEnabled || isLoading}
      className={`w-full rounded-[5px] h-[50px] flex items-center justify-center gap-2.5 mb-[clamp(1.25rem,5vw,1.5rem)] transition-all duration-200 ${
        isEnabled && !isLoading
          ? enabledClass
          : 'bg-border text-muted-foreground cursor-not-allowed'
      }`}
    >
      {isLoading ? (
        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      )}
      <span className="text-[15px] font-bold">{buttonText}</span>
    </button>
  );
}
